
import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Space, Switch, Tabs, message } from "antd";
import Table, { ColumnType, TableProps } from "antd/es/table";
import StringUtils from "../../../../../../../util/StringUtils";
import { CreateTenantUserWizardWrapper, FormWrapper, OperationWrapper, Title, TitleWrapper, UserInformationWrapper, UserRolesWrapper } from "./CreateTenantUserWizard.styled";
import TenantUserApi from "../../../../../../../api/TenantUserApi";
import { ToolBarWrapper } from "../TenantUser.styled";
import { PlusOutlined } from "@ant-design/icons";

interface CreateUserWizardProps {
    tenantId: string,
    editingUserId?: string,

    onClose: ()=> void,
    onComplete: ()=> void
}

const CreateTenantUserWizard: React.FC<CreateUserWizardProps> = (props)=> {
    // api
    let userApi = new TenantUserApi();
    const [messageApi, contextHolder] = message.useMessage();

    // form
    const [form] = Form.useForm();

    // state
    let [userView, setUserView] = useState<any>({});
    let [userRoles, setUserRoles] = useState([]);
    let [tenantRoles, setTenantRoles] = useState([]);
    let [openRoleSelectionModal, setOpenRoleSelectionModal] = useState(false);
    let [submitEnabled, setSubmitEnabled] = useState(false);
    let [loading, setLoading] = useState(false);

    // functions
    let loadUserProfileAndPermissions = async ()=> {
        // user view
        let userView = (await userApi.getTenantUserView(props.tenantId, props.editingUserId)).data.data;
        form.setFieldsValue(userView.user);
        setUserView(userView.user);

        // user roles
        let userRoles = userView.roles;
        setUserRoles(userRoles);
    }

    let loadTenantRoles = async ()=> {
        // tenant roles
        let tenantRoles = (await userApi.getTenantRoles(props.tenantId)).data.data;
        setTenantRoles(tenantRoles);
    }

    // event
    let onNameChange = (e)=> {
        userView.name = e.target.value;
        validateForm();
    };

    let onPhoneNumberChange = (e)=> {
        userView.phoneNumber = e.target.value;
        validateForm();
    };

    let onEmailChange = (e)=> {
        userView.phoneNumber = e.target.value;
        validateForm();
    };

    let onAdminChange = (value)=> {
        userView.admin = value;
        validateForm();
    };

    let onPasswordChange = (e)=> {
        userView.password = e.target.value;
        validateForm();
    }
    
    let onRemarksChange = (e)=> {
        userView.remarks = e.target.value;
        validateForm();
    };

    let onSubmit = async ()=> {
        // save
        let userId = props.editingUserId;
        if (props.editingUserId) {
            await userApi.updateTenantUser(props.tenantId, userView);
        } else {
            let user = (await userApi.createTenantUser(props.tenantId, userView)).data.data;
            userId = user.id;
        }

        // set user roles
        let roleIds = userRoles.map(userRole => userRole.id);
        await userApi.setTenantUserRoles(props.tenantId, userId, roleIds);

        // message
        messageApi.success("保存成功");

        // callback
        props.onComplete();
        props.onClose();
    };

    let onCancel = ()=> {
        props.onClose();
    };

    let onDeleteRole = (tenantRole: any)=> {
        userRoles = userRoles.filter(userRole => userRole.id !== tenantRole.id);
        setUserRoles(userRoles);
    };

    // validate
    let validateForm = ()=> {
        setUserView({...userView});
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            console.log(err);
        });
    }

    // effect
    useEffect(()=> {
        if (props.editingUserId) {
            loadUserProfileAndPermissions();
        }
        loadTenantRoles();
    }, [props.editingUserId])

    // render
    
    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '名称',
            dataIndex: 'role.name',
            key: 'role.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.name}</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'role.remarks',
            key: 'role.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    {<Popconfirm
                            title="删除角色"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认要删除么?</div> }
                            onConfirm={()=> onDeleteRole(record)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>删除</a>
                    </Popconfirm> }
                </Space>
            }
        }
    ];

    let roleSelectionColumns: ColumnType<any>[] = [
        {
            title: '名称',
            dataIndex: 'role.name',
            key: 'role.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.name}</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'role.remarks',
            key: 'role.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.remarks)}</span>
            }
        },
    ];

    const tenantRolesRowSelection: TableProps<any>['rowSelection'] = {
        type: 'radio',
        selectedRowKeys: [],
        onSelect: (record) => {
            // add to user roles
            let alreadyExist = false;
            for(let userRole of userRoles) {
                if (userRole.id === record.id) {
                    alreadyExist = true;
                    break;
                }
            }
            if (!alreadyExist) {
                userRoles.push(record);
                setUserRoles([...userRoles]);
            }

            // hide modal
            setOpenRoleSelectionModal(false);

            // validate form
            validateForm();
        },
    };


    let tabItems = [
        {
            key: 'user-information',
            label: '基本信息',
            children: <UserInformationWrapper>
                <FormWrapper>
                    <Form form={form} name="validateOnly" layout="vertical">
                        <Form.Item name="name" label="用户名" rules={[{required: true}]} initialValue={userView.name}>
                            <Input value={userView.name} onChange={onNameChange} placeholder="请输入用户名" />
                        </Form.Item>

                        <Form.Item name="phoneNumber" label="电话号码" rules={[{required: false}]} initialValue={userView.phoneNumber}>
                            <Input value={userView.phoneNumber} onChange={onPhoneNumberChange} placeholder="请输入手机号" />
                        </Form.Item>

                        <Form.Item name="email" label="电子邮箱" rules={[{required: false}]} initialValue={userView.email}>
                            <Input value={userView.phoneNumber} onChange={onEmailChange} placeholder="请输入电子邮箱" />
                        </Form.Item>

                        <Form.Item name="admin" label="管理员用户" rules={[{required: false}]} initialValue={userView.admin}>
                            <Switch checked={userView.admin} onChange={onAdminChange}/> 
                        </Form.Item>

                        <Form.Item name="password" label="密码" rules={[{required: props.editingUserId ? false: true}]} initialValue={userView.password}>
                            <Input.Password value={userView.password} onChange={onPasswordChange} placeholder="请输入密码" autoComplete="false" />
                        </Form.Item>

                        <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={userView.remarks}>
                            <Input.TextArea value={userView.remarks} style={{height: '100px'}} onChange={onRemarksChange} placeholder="请输入用户备注"/>
                        </Form.Item>
                    </Form>
                </FormWrapper>
            </UserInformationWrapper>
        },
        {
            key: 'user-roles',
            label: '用户角色',
            children: <UserRolesWrapper>
                <ToolBarWrapper>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenRoleSelectionModal(true)}>新增角色</Button>
                    <Modal open={openRoleSelectionModal} 
                        onCancel={()=> setOpenRoleSelectionModal(false)} 
                        onOk={()=> setOpenRoleSelectionModal(false)}
                        title="选择角色"
                        cancelButtonProps={{style: {display: 'none'}}}
                        closable={false}
                        destroyOnClose={true}
                        width={1000}
                        >
                        
                        <Table
                            rowKey={(record) => record.id}
                            columns={roleSelectionColumns}
                            dataSource={tenantRoles}
                            pagination={null}
                            loading={loading}
                            rowSelection={tenantRolesRowSelection}
                            onRow={(record)=> {
                                return {
                                    onClick: (e)=> {
                                        tenantRolesRowSelection.onSelect(record, null, null, null);
                                    }
                                }
                            }}
                        />
                        
                    </Modal>
                </ToolBarWrapper>
                <Table
                    rowKey={(record) => record.id}
                    columns={columns}
                    dataSource={userRoles}
                    pagination={null}
                    loading={loading}
                />
            </UserRolesWrapper>
        },
    ];

    return (
        <CreateTenantUserWizardWrapper>
            <TitleWrapper>
                <Title>{props.editingUserId ? '编辑用户' : '新增用户'}</Title>
            </TitleWrapper>

            <Tabs
                defaultActiveKey={'user-information'}
                items={tabItems}
                style={{flex: '1 1 auto', overflow: 'hidden'}}
            />

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </CreateTenantUserWizardWrapper>
    )
}

export default CreateTenantUserWizard;
